import { oc } from "@orpc/contract";
import {
  MetadataChangeSetEventAction,
  MetadataChangeSetStatus,
  MetadataChangeSetVersionSource,
} from "@erp/shared-types";
import { z } from "zod";
import {
  As400ExportGroupSchema,
  As400ExportRowSourceSchema,
  CatalogAdvancedFilterSchema,
  ChangeSetNewRowSchema,
  refineCatalogSearch,
} from "./products.contract";

const UUIDSchema = z.uuid();

export const MetadataChangeSetSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  description: z.string().nullable(),
  status: z.nativeEnum(MetadataChangeSetStatus),
  createdByUserId: UUIDSchema.nullable(),
  assigneeId: UUIDSchema.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const ListMetadataChangeSetsResponseSchema = z.object({
  changeSets: z.array(MetadataChangeSetSchema),
});

export const GetMetadataChangeSetInputSchema = z.object({
  id: UUIDSchema,
});

// The change set's metadata only. Its contents are browsed through the
// changeSetId-scoped catalog endpoints (products.listCatalog,
// products.listAs400Export, raw-data records), which carry the staged-change
// overlays.
export const GetMetadataChangeSetResponseSchema = z.object({
  changeSet: MetadataChangeSetSchema.nullable(),
});

// Name/description shared by both staging entry points.
const StageChangeSetBaseSchema = z.object({
  name: z.string().trim().min(1).max(255),
  description: z.string().trim().max(2000).optional(),
});

// Stage an explicit selection of catalog products as a new change set. The
// client only sends product keys, so a selection survives paging.
export const StageSelectedChangesInputSchema = StageChangeSetBaseSchema.extend({
  productKeys: z.array(z.string().min(1)).min(1).max(50000),
});

// Stage every product matching the current catalog search + filters (resolved
// server-side, streamed in batches). Unbounded — the client warns above the
// staging threshold using the catalog's total count.
export const StageAllChangesInputSchema = StageChangeSetBaseSchema.extend({
  search: z.string().trim().optional(),
  searchMode: z.enum(["text", "regex"]).default("text"),
  advancedFilters: z.array(CatalogAdvancedFilterSchema).max(20).optional(),
}).superRefine(refineCatalogSearch);

// Stage every product of an org-shared item list as a new change set. The
// list's product keys are streamed server-side — they never transit the client.
export const StageFromItemListInputSchema = StageChangeSetBaseSchema.extend({
  itemListId: UUIDSchema,
});

export const StageChangesResponseSchema = z.object({
  changeSetId: UUIDSchema,
  // Number of products staged into the new change set.
  stagedCount: z.number().int(),
});

export const UpdateMetadataChangeSetInputSchema = z
  .object({
    id: UUIDSchema,
    name: z.string().trim().min(1).max(255).optional(),
    description: z.string().trim().max(2000).nullable().optional(),
    status: z.nativeEnum(MetadataChangeSetStatus).optional(),
    // null clears the assignment; a UUID assigns to that user.
    assigneeId: UUIDSchema.nullable().optional(),
  })
  .refine(
    (input) =>
      input.name !== undefined ||
      input.description !== undefined ||
      input.status !== undefined ||
      input.assigneeId !== undefined,
    { message: "At least one field to update must be provided" },
  );

export const UpdateMetadataChangeSetResponseSchema = z.object({
  changeSet: MetadataChangeSetSchema,
});

// The changed cells shared by both staged-edit shapes: `values` keyed by
// canonical field code (a null value clears the field), and `originalValues`
// snapshotting the live cell values at edit time — the drift baseline stored
// with the staged change.
const stagedEditCells = {
  values: z.record(z.string().min(1), z.string().nullable()),
  originalValues: z.record(z.string().min(1), z.string().nullable()),
};
const hasChangedField = (edit: { values: Record<string, string | null> }) =>
  Object.keys(edit.values).length > 0;
const AT_LEAST_ONE_FIELD = {
  message: "An edit must change at least one field",
};

// One edited export row: the ingest source row(s) it was assembled from, plus
// the changed cells.
export const StagedExportRowEditSchema = z
  .object({
    sources: z.array(As400ExportRowSourceSchema).min(1).max(8),
    ...stagedEditCells,
  })
  .refine(hasChangedField, AT_LEAST_ONE_FIELD);

// Stage inline edits made in the review page's Export tab. The group +
// dataSourceIds identify the flattened view the rows came from, so the server
// can attribute each edited column to the ingest table that owns it. Several
// ids when the view combines divisions — each edited row's own sources pick out
// the division it belongs to, so attribution stays correct.
export const StageDataLinkEditsInputSchema = z.object({
  changeSetId: UUIDSchema,
  group: As400ExportGroupSchema,
  dataSourceIds: z.array(UUIDSchema).min(1).max(50),
  edits: z.array(StagedExportRowEditSchema).min(1).max(500),
});

export const StageDataLinkEditsResponseSchema = z.object({
  // Number of ingest rows whose staged change was written (or removed, when
  // every edited field reverted to its live value).
  savedRowCount: z.number().int(),
});

// One edited raw record from the Search-tab AS/400 view: its ingest row
// identity (dataSourceLinkId + dataId) and the changed cells. Unlike the
// worksheet edits above, each row IS a single ingest record, so no column→table
// attribution is needed — the field goes straight to that record.
export const StagedRawRowEditSchema = z
  .object({
    dataSourceLinkId: UUIDSchema,
    dataId: UUIDSchema,
    ...stagedEditCells,
  })
  .refine(hasChangedField, AT_LEAST_ONE_FIELD);

// Stage inline edits made in the review page's Search-tab AS/400 view, where
// each edited row maps directly to one ingest record.
export const StageRawDataEditsInputSchema = z.object({
  changeSetId: UUIDSchema,
  edits: z.array(StagedRawRowEditSchema).min(1).max(500),
});

export const StageRawDataEditsResponseSchema = z.object({
  savedRowCount: z.number().int(),
});

// ─── New rows ─────────────────────────────────────────────────────────────────
// Net-new worksheet rows authored on a review: products the AS/400 doesn't
// have yet, staged per export worksheet + division so the upload file can
// create them. Values are keyed by the worksheet column's edit key and
// validated server-side; the group's item-number column is required
// (workbook-normalized: upper case, no spaces) and unique within the review.

export const AddChangeSetNewRowInputSchema = z.object({
  changeSetId: UUIDSchema,
  group: As400ExportGroupSchema,
  dataSourceId: UUIDSchema,
  values: z.record(z.string().min(1), z.string().nullable()),
});

export const AddChangeSetNewRowResponseSchema = z.object({
  row: ChangeSetNewRowSchema,
});

// Merge cell edits into existing new rows (a null value unsets the cell; the
// item-number column can change but never blank).
export const UpdateChangeSetNewRowsInputSchema = z.object({
  changeSetId: UUIDSchema,
  edits: z
    .array(
      z
        .object({
          id: UUIDSchema,
          values: z.record(z.string().min(1), z.string().nullable()),
        })
        .refine(hasChangedField, AT_LEAST_ONE_FIELD),
    )
    .min(1)
    .max(500),
});

export const UpdateChangeSetNewRowsResponseSchema = z.object({
  savedRowCount: z.number().int(),
});

export const DeleteChangeSetNewRowInputSchema = z.object({
  changeSetId: UUIDSchema,
  id: UUIDSchema,
});

export const DeleteChangeSetNewRowResponseSchema = z.object({
  success: z.boolean(),
});

// One change-set-level history event: what happened to the change set (not to
// an individual staged row), who did it, and when. `metadata` carries the
// action's detail, e.g. { from, to } for status changes or { savedRowCount }
// for staged-edit saves.
export const MetadataChangeSetEventSchema = z.object({
  id: UUIDSchema,
  action: z.nativeEnum(MetadataChangeSetEventAction),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  actorUserId: UUIDSchema.nullable(),
  // Display name of the acting user; null for system actions or deleted users.
  actorName: z.string().nullable(),
  createdAt: z.string(),
});

export const ListMetadataChangeSetEventsInputSchema = z.object({
  changeSetId: UUIDSchema,
});

export const ListMetadataChangeSetEventsResponseSchema = z.object({
  // Newest first.
  events: z.array(MetadataChangeSetEventSchema),
});

// One saved state of a change set's staged edits. The snapshot itself stays
// server-side — the list only describes each version well enough to pick one.
export const MetadataChangeSetVersionSchema = z.object({
  id: UUIDSchema,
  // 1-based, per change set.
  versionNumber: z.number().int().positive(),
  // Ingest rows carrying a staged edit in this version, and the changed fields
  // across them.
  rowCount: z.number().int().nonnegative(),
  fieldCount: z.number().int().nonnegative(),
  source: z.nativeEnum(MetadataChangeSetVersionSource),
  // Whether this version is the change set's live staged state.
  isCurrent: z.boolean(),
  // Display name of the version's author; null for system or deleted users.
  createdByName: z.string().nullable(),
  createdAt: z.string(),
});

export const ListMetadataChangeSetVersionsInputSchema = z.object({
  changeSetId: UUIDSchema,
});

export const ListMetadataChangeSetVersionsResponseSchema = z.object({
  // Newest first.
  versions: z.array(MetadataChangeSetVersionSchema),
});

export const RestoreMetadataChangeSetVersionInputSchema = z.object({
  changeSetId: UUIDSchema,
  versionId: UUIDSchema,
});

export const RestoreMetadataChangeSetVersionResponseSchema = z.object({
  versionId: UUIDSchema,
  versionNumber: z.number().int().positive(),
});

export const DeleteMetadataChangeSetInputSchema = z.object({ id: UUIDSchema });

export const DeleteMetadataChangeSetResponseSchema = z.object({
  success: z.boolean(),
});

export const metadataChangeSetsContracts = {
  list: oc
    .input(z.object({}))
    .output(ListMetadataChangeSetsResponseSchema)
    .route({
      method: "GET",
      summary: "List metadata change sets for the current org",
    }),
  get: oc
    .input(GetMetadataChangeSetInputSchema)
    .output(GetMetadataChangeSetResponseSchema)
    .route({
      method: "GET",
      summary: "Get a metadata change set",
    }),
  stageSelected: oc
    .input(StageSelectedChangesInputSchema)
    .output(StageChangesResponseSchema)
    .route({
      method: "POST",
      summary: "Stage selected catalog products as a new change set",
    }),
  stageAll: oc
    .input(StageAllChangesInputSchema)
    .output(StageChangesResponseSchema)
    .route({
      method: "POST",
      summary:
        "Stage every product matching the current catalog filters as a new change set",
    }),
  stageFromItemList: oc
    .input(StageFromItemListInputSchema)
    .output(StageChangesResponseSchema)
    .route({
      method: "POST",
      summary: "Stage an item list's products as a new change set",
    }),
  stageDataLinkEdits: oc
    .input(StageDataLinkEditsInputSchema)
    .output(StageDataLinkEditsResponseSchema)
    .route({
      method: "POST",
      summary:
        "Stage inline export-view edits as data-link changes on a change set",
    }),
  stageRawDataEdits: oc
    .input(StageRawDataEditsInputSchema)
    .output(StageRawDataEditsResponseSchema)
    .route({
      method: "POST",
      summary:
        "Stage inline AS/400-view raw-record edits as data-link changes on a change set",
    }),
  addNewRow: oc
    .input(AddChangeSetNewRowInputSchema)
    .output(AddChangeSetNewRowResponseSchema)
    .route({
      method: "POST",
      summary: "Author a net-new worksheet row on a change set",
    }),
  updateNewRows: oc
    .input(UpdateChangeSetNewRowsInputSchema)
    .output(UpdateChangeSetNewRowsResponseSchema)
    .route({
      method: "POST",
      summary: "Merge cell edits into a change set's new rows",
    }),
  deleteNewRow: oc
    .input(DeleteChangeSetNewRowInputSchema)
    .output(DeleteChangeSetNewRowResponseSchema)
    .route({
      method: "POST",
      summary: "Remove a new row from a change set",
    }),
  listEvents: oc
    .input(ListMetadataChangeSetEventsInputSchema)
    .output(ListMetadataChangeSetEventsResponseSchema)
    .route({
      method: "GET",
      summary: "List a change set's history events, newest first",
    }),
  listVersions: oc
    .input(ListMetadataChangeSetVersionsInputSchema)
    .output(ListMetadataChangeSetVersionsResponseSchema)
    .route({
      method: "GET",
      summary: "List a change set's saved versions, newest first",
    }),
  restoreVersion: oc
    .input(RestoreMetadataChangeSetVersionInputSchema)
    .output(RestoreMetadataChangeSetVersionResponseSchema)
    .route({
      method: "POST",
      summary:
        "Restore a change set's staged edits to a previous version and make it current",
    }),
  update: oc
    .input(UpdateMetadataChangeSetInputSchema)
    .output(UpdateMetadataChangeSetResponseSchema)
    .route({
      method: "PATCH",
      summary: "Update a metadata change set's name, description, or status",
    }),
  delete: oc
    .input(DeleteMetadataChangeSetInputSchema)
    .output(DeleteMetadataChangeSetResponseSchema)
    .route({
      method: "DELETE",
      summary: "Delete a metadata change set and its staged changes",
    }),
} as const;

export type MetadataChangeSetDTO = z.infer<typeof MetadataChangeSetSchema>;
export type ListMetadataChangeSetsResponse = z.infer<
  typeof ListMetadataChangeSetsResponseSchema
>;
export type GetMetadataChangeSetResponse = z.infer<
  typeof GetMetadataChangeSetResponseSchema
>;
export type StageSelectedChangesInput = z.infer<
  typeof StageSelectedChangesInputSchema
>;
export type StageAllChangesInput = z.infer<typeof StageAllChangesInputSchema>;
export type StageFromItemListInput = z.infer<
  typeof StageFromItemListInputSchema
>;
export type StageChangesResponse = z.infer<typeof StageChangesResponseSchema>;
export type StagedExportRowEdit = z.infer<typeof StagedExportRowEditSchema>;
export type StageDataLinkEditsInput = z.infer<
  typeof StageDataLinkEditsInputSchema
>;
export type StageDataLinkEditsResponse = z.infer<
  typeof StageDataLinkEditsResponseSchema
>;
export type StagedRawRowEdit = z.infer<typeof StagedRawRowEditSchema>;
export type StageRawDataEditsInput = z.infer<
  typeof StageRawDataEditsInputSchema
>;
export type StageRawDataEditsResponse = z.infer<
  typeof StageRawDataEditsResponseSchema
>;
export type MetadataChangeSetEventDTO = z.infer<
  typeof MetadataChangeSetEventSchema
>;
export type ListMetadataChangeSetEventsResponse = z.infer<
  typeof ListMetadataChangeSetEventsResponseSchema
>;
export type MetadataChangeSetVersionDTO = z.infer<
  typeof MetadataChangeSetVersionSchema
>;
export type ListMetadataChangeSetVersionsResponse = z.infer<
  typeof ListMetadataChangeSetVersionsResponseSchema
>;
export type RestoreMetadataChangeSetVersionInput = z.infer<
  typeof RestoreMetadataChangeSetVersionInputSchema
>;
export type RestoreMetadataChangeSetVersionResponse = z.infer<
  typeof RestoreMetadataChangeSetVersionResponseSchema
>;
export type UpdateMetadataChangeSetInput = z.infer<
  typeof UpdateMetadataChangeSetInputSchema
>;
export type UpdateMetadataChangeSetResponse = z.infer<
  typeof UpdateMetadataChangeSetResponseSchema
>;
export type DeleteMetadataChangeSetResponse = z.infer<
  typeof DeleteMetadataChangeSetResponseSchema
>;
export type AddChangeSetNewRowInput = z.infer<
  typeof AddChangeSetNewRowInputSchema
>;
export type AddChangeSetNewRowResponse = z.infer<
  typeof AddChangeSetNewRowResponseSchema
>;
export type UpdateChangeSetNewRowsInput = z.infer<
  typeof UpdateChangeSetNewRowsInputSchema
>;
export type UpdateChangeSetNewRowsResponse = z.infer<
  typeof UpdateChangeSetNewRowsResponseSchema
>;
export type DeleteChangeSetNewRowInput = z.infer<
  typeof DeleteChangeSetNewRowInputSchema
>;
export type DeleteChangeSetNewRowResponse = z.infer<
  typeof DeleteChangeSetNewRowResponseSchema
>;
